import { Linkedin, Instagram } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { Reveal, Overline } from "../Reveal";
import FieldLines from "../FieldLines";

export default function Talents() {
    const { site, tx } = useSite();
    const c = site?.content || {};
    const talents = site?.talents || [];

    if (!talents.length) return null;

    return (
        <section id="talent" data-testid="talents-section" className="grain relative overflow-hidden bg-[#0A0A0A] px-6 py-32 text-white md:px-12 md:py-48 lg:px-24">
            <FieldLines className="absolute -left-64 bottom-0 h-[38rem] w-[38rem] opacity-40" />
            <div className="relative flex flex-wrap items-end justify-between gap-10">
                <div>
                    <Overline testId="talents-overline">{tx(c.talents_overline) || "TALENT NETWORK — 03"}</Overline>
                    <Reveal>
                        <h2 className="font-display mt-6 text-[clamp(3rem,7.5vw,7rem)] leading-[0.85]" data-testid="talents-title">
                            {tx(c.talents_title1) || "SOME OF OUR"}
                            <br />
                            <span className="text-[#D60024]">{tx(c.talents_title2) || "TALENTS."}</span>
                        </h2>
                    </Reveal>
                </div>
                <Reveal delay={0.15}>
                    <p className="max-w-sm text-base leading-relaxed text-white/65" data-testid="talents-text">
                        {tx(c.talents_text)}
                    </p>
                </Reveal>
            </div>

            <div className="relative mt-20 grid grid-cols-1 gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-4">
                {talents.map((t, i) => (
                    <Reveal key={t.id} delay={(i % 4) * 0.08}>
                        <article data-testid={`talent-card-${i}`} data-cursor="plus" className="group">
                            <div className="relative overflow-hidden bg-[#141414]">
                                <img
                                    src={t.photo_url}
                                    alt={t.name}
                                    loading="lazy"
                                    className="aspect-[3/4] w-full object-cover grayscale transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0"
                                />
                                <span className="font-display absolute left-4 top-3 text-2xl text-white/75" aria-hidden="true">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span
                                    className="font-display absolute bottom-3 right-4 text-3xl text-[#D60024] opacity-0 transition-all duration-500 group-hover:rotate-45 group-hover:opacity-100"
                                    aria-hidden="true"
                                >
                                    +
                                </span>
                            </div>
                            <h3 className="font-display mt-5 text-3xl leading-none transition-colors duration-300 group-hover:text-[#D60024]">{t.name}</h3>
                            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D60024]">{tx(t.role)}</p>
                            <p className="mt-3 text-sm leading-relaxed text-white/55 line-clamp-3">{tx(t.bio)}</p>
                            <div className="mt-4 flex items-center gap-5">
                                {t.website && (
                                    <a
                                        href={t.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`talent-link-${i}`}
                                        className="u-line text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 hover:text-white"
                                    >
                                        {tx({ en: "LEARN MORE", pt: "SABER MAIS" })} +
                                    </a>
                                )}
                                <span className="flex items-center gap-3 text-white/40">
                                    {t.linkedin && (
                                        <a href={t.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid={`talent-linkedin-${i}`} className="transition-colors hover:text-[#D60024]">
                                            <Linkedin size={14} />
                                        </a>
                                    )}
                                    {t.instagram && (
                                        <a href={t.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid={`talent-instagram-${i}`} className="transition-colors hover:text-[#D60024]">
                                            <Instagram size={14} />
                                        </a>
                                    )}
                                </span>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}
